import { getAreaName } from '../lib/area';
import type { Issue, Lookups, Me, UserRef } from '@ft/shared';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loading, PmChips, useLoad, useToast } from '../components/ui';
import { canEdit } from '../lib/area';
import { api } from '../lib/http';
import { getLang, useI18n, type T } from '../i18n';
import { dfmt, eur, pct, todayIso } from '../lib/format';
import { Chip, CollapseAll, Panel } from '../lib/collapse';

const TONE = { neg: 'neg', crit: 'crit', info: 'neu' } as const;

/** '2026-10' → „Oktober 2026“, '2026-Q3' → „Q3 2026“ */
function periodLabel(v: string): string {
  if (/^\d{4}-Q\d$/.test(v)) return `${v.slice(5)} ${v.slice(0, 4)}`;
  if (/^\d{4}-\d{2}$/.test(v)) return new Date(Date.UTC(Number(v.slice(0, 4)), Number(v.slice(5, 7)) - 1, 1)).toLocaleDateString(getLang() === 'en' ? 'en-GB' : 'de-DE', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  return v;
}

/** Text eines offenen Punkts in der gewählten Sprache. Beträge kommen in Cent. */
export function issueText(i: Issue, t: T): { title: string; detail: string } {
  const p = i.params;
  const n = (k: string) => Number(p[k] ?? 0);
  const order = `${p.order || t('common.noNumber')}${p.warranty ? t('issues.warrantySuffix') : ''}`;
  const vals = {
    ...p, order,
    billed: eur(n('billed')), volume: eur(n('volume')), rest: eur(n('volume') - n('billed')),
    ordered: eur(n('ordered')), budget: eur(n('budget')), over: eur(n('ordered') - n('budget')),
    missing: [p.link ? '' : t('ct.missingLink'), p.pdf ? '' : t('ct.missingPdf')].filter(Boolean).join(t('ct.and')),
    pct: n('volume') ? pct(n('billed') / n('volume')) : '–',
    date: p.date ? dfmt(String(p.date)) : '',
    due: eur(n('due')),
    amount: eur(n('amount')), released: eur(n('released')),
    period: periodLabel(String(p.period ?? '')),
  };
  return { title: t(`issue.${i.code}.t`, vals), detail: t(`issue.${i.code}.d`, vals) };
}

/** Eine Zeile der Liste: ein Punkt oder ein Bündel gleichartiger Punkte (z. B. alle auslaufenden Bestellungen eines Projekts). */
interface Row { key: string; level: Issue['level']; items: Issue[]; title: string; detail: string; link: string; pms: UserRef[]; mail: { subject: string; body: string } | null }

const LEVEL_RANK = { neg: 0, crit: 1, info: 2 } as const;

/** Bündelt „Laufzeit endet“ je Projekt und „Vertragsart fehlt“ zu je einer Zeile; alles andere bleibt einzeln. */
export function bundleIssues(list: Issue[], t: T, me: { name: string }): Row[] {
  const rows: Row[] = [];
  const ending = new Map<string, Issue[]>();
  const types: Issue[] = [];
  for (const i of list) {
    if (i.code === 'endingSoon') { const p = String(i.params.projects ?? ''); ending.set(p, [...(ending.get(p) ?? []), i]); continue; }
    if (i.code === 'contractTypeMissing') { types.push(i); continue; }
    const x = issueText(i, t);
    rows.push({ key: i.key, level: i.level, items: [i], title: x.title, detail: x.detail, link: i.link, pms: i.assignee ? [i.assignee] : i.pms, mail: mailFor([i], t, me) });
  }
  for (const [project, xs] of ending) {
    if (xs.length === 1) { const x = issueText(xs[0]!, t); rows.push({ key: xs[0]!.key, level: xs[0]!.level, items: xs, title: x.title, detail: x.detail, link: xs[0]!.link, pms: xs[0]!.assignee ? [xs[0]!.assignee] : xs[0]!.pms, mail: mailFor(xs, t, me) }); continue; }
    const level = xs.some((i) => i.level === 'crit') ? 'crit' : 'info';
    const dates = [...new Set(xs.map((i) => String(i.params.date)))].sort();
    rows.push({
      key: `bundle:ending|${project}`, level, items: xs,
      title: t('issues.bundle.ending', { project, n: xs.length, date: dfmt(dates[0]) }),
      detail: t('issues.bundle.endingD', { orders: xs.map((i) => i.params.order).join(', ') }),
      link: `/bestellungen?projekt=${encodeURIComponent(project.split(', ')[0] ?? '')}`,
      pms: [...new Map(xs.flatMap((i) => (i.assignee ? [i.assignee] : i.pms)).map((u) => [u.id, u])).values()],
      mail: mailFor(xs, t, me),
    });
  }
  if (types.length > 1) {
    rows.push({ key: 'bundle:contractType', level: 'info', items: types, title: t('issues.bundle.types', { n: types.length }), detail: types.map((i) => i.params.contract).join(' · '), link: '/vertraege', pms: [...new Map(types.flatMap((i) => i.pms).map((u) => [u.id, u])).values()], mail: null });
  } else if (types.length === 1) {
    const x = issueText(types[0]!, t);
    rows.push({ key: types[0]!.key, level: 'info', items: types, title: x.title, detail: x.detail, link: types[0]!.link, pms: types[0]!.pms, mail: null });
  }
  return rows.sort((a, b) => LEVEL_RANK[a.level] - LEVEL_RANK[b.level]);
}

/** Mailentwurf für Punkte, bei denen man Kunde oder Einkauf anschreibt (Empfänger trägt man selbst ein). */
export function mailFor(xs: Issue[], t: T, me: { name: string }): Row['mail'] {
  const i = xs[0]!;
  const p = i.params;
  const n = (k: string) => Number(p[k] ?? 0);
  if (i.code === 'invoiceOverdue') return { subject: t('mail.overdue.s', { invoice: String(p.invoice) }), body: t('mail.overdue.b', { invoice: String(p.invoice), project: String(p.project), due: eur(n('due')), date: dfmt(String(p.date)), me: me.name }) };
  if (i.code === 'nearlyUsed') return { subject: t('mail.nearly.s', { order: String(p.order) }), body: t('mail.nearly.b', { order: String(p.order), volume: eur(n('volume')), pct: n('volume') ? pct(n('billed') / n('volume')) : '–', rest: eur(n('volume') - n('billed')), me: me.name }) };
  if (i.code === 'endingSoon') return { subject: t('mail.ending.s', { project: String(p.projects) }), body: t('mail.ending.b', { project: String(p.projects), orders: xs.map((x) => x.params.order).join(', '), date: dfmt(String(p.date)), me: me.name }) };
  if (i.code === 'contractNearlyUsed' || i.code === 'contractEnding') return { subject: t('mail.contract.s', { contract: String(p.contract) }), body: t('mail.contract.b', { contract: String(p.contract), me: me.name }) };
  return null;
}

const plusDays = (d: number) => { const x = new Date(); x.setDate(x.getDate() + d); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`; };
const monthEnd = () => { const x = new Date(); const e = new Date(x.getFullYear(), x.getMonth() + 1, 0); return `${e.getFullYear()}-${String(e.getMonth() + 1).padStart(2, '0')}-${String(e.getDate()).padStart(2, '0')}`; };

/** Offene Punkte werden laufend aus den Daten berechnet und verschwinden, sobald sie behoben sind. Notizen, Zurückstellen und Zuständige kommen dazu. */
export function IssuesPage({ me }: { me: Me }) {
  const { t } = useI18n();
  const { data, error, reload } = useLoad<Issue[]>('/api/issues');
  const editable = canEdit(me);
  const { data: lookups } = useLoad<Lookups>(editable ? '/api/lookups' : null);
  const [mine, setMine] = useState(false);
  const [hideSnoozed, setHideSnoozed] = useState(false);
  const [openRow, setOpenRow] = useState<{ key: string; what: 'note' | 'mail' | 'snooze' } | null>(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, say] = useToast();
  const list = (data ?? []).filter((i) => !mine || i.pms.some((p) => p.id === me.id) || i.assignee?.id === me.id);
  const rows = bundleIssues(list, t, me);
  const snoozed = (r: Row) => r.items.every((i) => !!i.snoozedUntil);
  const visible = rows.filter((r) => !hideSnoozed || !snoozed(r));
  const nSnoozed = rows.filter(snoozed).length;
  const people = (lookups?.users ?? []).filter((u) => u.role !== 'reader');

  async function each(r: Row, fn: (i: Issue) => Promise<unknown>, done: string) {
    setBusy(true);
    try { for (const i of r.items) await fn(i); setOpenRow(null); setText(''); reload(); say(done); } catch (e) { say((e as Error).message); } finally { setBusy(false); }
  }
  const addNote = (r: Row) => each(r, (i) => api('POST', '/api/issues/notes', { key: i.key, text: text.trim() }), t('issues.noteSaved'));
  const setState = (r: Row, snoozedUntil: string | null, assigneeId: string | null) =>
    each(r, (i) => api('PUT', '/api/issues/state', { key: i.key, snoozedUntil, assigneeId }), snoozedUntil ? t('issues.snoozedOk', { d: dfmt(snoozedUntil) }) : t('issues.stateSaved'));

  return (
    <section className="page" style={{ maxWidth: 1080 }}>
      <div className="pagehead">
        <div><div className="crumb">{t('crumb.root', { area: getAreaName() })}</div><h1>{t('issues.title')}</h1></div>
        <span className="spacer" />
        <CollapseAll ids={['issues.neg', 'issues.crit', 'issues.info'].filter((id) => visible.some((r) => `issues.${r.level}` === id))} />
        {nSnoozed > 0 && <label className="small muted row" style={{ gap: 6 }}><input type="checkbox" checked={hideSnoozed} onChange={(e) => setHideSnoozed(e.target.checked)} />{t('issues.hideSnoozed', { n: nSnoozed })}</label>}
        <div className="seg" role="group" aria-label={t('common.filter')}>
          <button aria-pressed={!mine} onClick={() => setMine(false)}>{t('common.all')}</button>
          <button aria-pressed={mine} onClick={() => setMine(true)}>{t('common.myProjects')}</button>
        </div>
      </div>
      <p className="muted lead">{t('issues.lead')}</p>
      {!data ? <Loading error={error} /> : !visible.length ? <div className="box"><div className="empty">{t('issues.empty')}</div></div> : (
        (['neg', 'crit', 'info'] as const).map((lvl) => {
          const xs = visible.filter((r) => r.level === lvl).sort((a, b) => Number(snoozed(a)) - Number(snoozed(b)));
          if (!xs.length) return null;
          const active = xs.filter((r) => !snoozed(r));
          return (
            <Panel key={lvl} id={`issues.${lvl}`} title={`${t(`issues.level.${lvl}`)} (${active.length})`}
              summary={<Chip tone={lvl === 'info' ? undefined : lvl}><b>{active.length}</b> · {(active[0] ?? xs[0])!.title}{xs.length > 1 ? ' …' : ''}</Chip>}>
              <ul className="issues">
                {xs.map((r) => {
                  const sn = snoozed(r);
                  const until = r.items[0]!.snoozedUntil;
                  const notes = r.items.flatMap((i) => i.notes ?? []).sort((a, b) => b.at.localeCompare(a.at));
                  const assignee = r.items[0]!.assignee ?? null;
                  const isOpen = (w: 'note' | 'mail' | 'snooze') => openRow?.key === r.key && openRow.what === w;
                  return (
                    <li key={r.key} className={sn ? 'snoozed' : undefined}>
                      <span>{sn ? <span className="pill neu">{t('issues.until', { d: dfmt(until) })}</span> : <span className={`pill ${TONE[r.level]}`}>{t(`issues.level.${r.level}`)}</span>}</span>
                      <div>
                        <div className="t">{r.title}</div>
                        <div className="muted small">{r.detail}</div>
                        {notes.length > 0 && <div className="inotes">{notes.slice(0, 3).map((n) => <p key={n.id}>„{n.text}“ <span className="muted">– {n.by ?? '?'}, {dfmt(n.at)}</span></p>)}{notes.length > 3 && <p className="muted small">{t('issues.moreNotes', { n: notes.length - 3 })}</p>}</div>}
                        {isOpen('note') && (
                          <form className="inote" onSubmit={(e) => { e.preventDefault(); if (text.trim()) void addNote(r); }}>
                            <textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} rows={2} placeholder={t('issues.notePh')} autoFocus aria-label={t('issues.note')} />
                            <div className="row"><button className="btn primary" disabled={busy || !text.trim()}>{t('common.save')}</button><button type="button" className="btn ghost" onClick={() => setOpenRow(null)}>{t('common.cancel')}</button></div>
                          </form>
                        )}
                        {isOpen('snooze') && (
                          <div className="inote row">
                            <button className="btn" disabled={busy} onClick={() => void setState(r, plusDays(7), assignee?.id ?? null)}>{t('issues.snooze.week')}</button>
                            <button className="btn" disabled={busy} onClick={() => void setState(r, plusDays(14), assignee?.id ?? null)}>{t('issues.snooze.twoWeeks')}</button>
                            <button className="btn" disabled={busy} onClick={() => void setState(r, monthEnd(), assignee?.id ?? null)}>{t('issues.snooze.monthEnd')}</button>
                            <input type="date" min={todayIso()} aria-label={t('issues.snooze.date')} onChange={(e) => { if (e.target.value) void setState(r, e.target.value, assignee?.id ?? null); }} />
                            {editable && people.length > 0 && (
                              <select aria-label={t('issues.assignee')} value={assignee?.id ?? ''} onChange={(e) => void setState(r, until ?? null, e.target.value || null)}>
                                <option value="">{t('issues.assigneeDefault')}</option>
                                {people.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                              </select>
                            )}
                            <button type="button" className="btn ghost" onClick={() => setOpenRow(null)}>{t('common.close')}</button>
                          </div>
                        )}
                        {isOpen('mail') && r.mail && (
                          <div className="imail">
                            <div className="small"><b>{t('issues.mail.subject')}:</b> {r.mail.subject}</div>
                            <pre>{r.mail.body}</pre>
                            <div className="row">
                              <a className="btn primary" href={`mailto:?subject=${encodeURIComponent(r.mail.subject)}&body=${encodeURIComponent(r.mail.body)}`}>{t('issues.mail.open')}</a>
                              <button className="btn" onClick={() => { void navigator.clipboard?.writeText(`${r.mail!.subject}\n\n${r.mail!.body}`); say(t('issues.mail.copied')); }}>{t('issues.mail.copy')}</button>
                              <button className="btn ghost" onClick={() => setOpenRow(null)}>{t('common.close')}</button>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="row iacts">
                        {r.pms.length > 0 && <PmChips pms={assignee ? [assignee] : r.pms} />}
                        {editable && !sn && r.mail && <button className="btn" onClick={() => setOpenRow({ key: r.key, what: 'mail' })}>✉ {t(`issues.mailBtn.${r.items[0]!.code === 'invoiceOverdue' ? 'remind' : r.items[0]!.code === 'endingSoon' ? 'extend' : 'order'}`)}</button>}
                        {editable && <button className="btn" onClick={() => { setText(''); setOpenRow({ key: r.key, what: 'note' }); }}>{t('issues.addNote')}</button>}
                        {editable && (sn
                          ? <button className="btn" disabled={busy} onClick={() => void setState(r, null, assignee?.id ?? null)}>{t('issues.unsnooze')}</button>
                          : <button className="btn" onClick={() => setOpenRow({ key: r.key, what: 'snooze' })}>{t('issues.snoozeBtn')}</button>)}
                        {(me.role === 'admin' || !r.link.startsWith('/verwaltung')) && <Link className="btn ghost" to={r.link}>{t('common.open')}</Link>}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </Panel>
          );
        })
      )}
      {toast}
    </section>
  );
}
